import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  clearAuthStorage,
  getTokenExpiryMs,
  isTokenExpired,
  logoutSession,
  registerSessionExpiredHandler,
} from "../services/sessionService";
import { getSettings } from "../services/SettingsService";

const UserContext = createContext();
export const useGlobalContext = () => useContext(UserContext);

function getInitialToken() {
  const token = localStorage.getItem("token");
  if (!token || isTokenExpired(token)) {
    if (token) clearAuthStorage();
    return null;
  }
  return token;
}

function mapProfile(settings) {
  if (!settings) return null;
  return {
    name: settings.name || "",
    email: settings.email || "",
    profilePhoto: settings.profilePhoto || null,
  };
}

export const UserProvider = ({ children }) => {
  const [userToken, setUserToken] = useState(() => getInitialToken());
  const [showLogin, setShowLogin] = useState(() => !localStorage.getItem("token"));
  const [showMyBotSetup, setShowMyBotSetup] = useState(true);
  const [showCurrentlyRunning, setShowCurrentlyRunning] = useState(true);
  const [profile, setProfile] = useState(null);

  const refreshProfile = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token || isTokenExpired(token)) {
      setProfile(null);
      return null;
    }

    try {
      const settings = await getSettings();
      const nextProfile = mapProfile(settings);
      setProfile(nextProfile);
      return nextProfile;
    } catch (err) {
      console.error(err.response ? err.response.data : err.message);
      return null;
    }
  }, []);

  useEffect(() => {
    registerSessionExpiredHandler(() => {
      setUserToken(null);
      setShowLogin(true);
      setProfile(null);
    });
  }, []);

  useEffect(() => {
    if (!userToken) {
      setShowLogin(true);
      setProfile(null);
      return undefined;
    }

    setShowLogin(false);
    refreshProfile();

    if (isTokenExpired(userToken)) {
      logoutSession();
      return undefined;
    }

    const msUntilExpiry = getTokenExpiryMs(userToken) - Date.now();
    const timerId = setTimeout(() => logoutSession(), msUntilExpiry);

    const intervalId = setInterval(() => {
      if (isTokenExpired(userToken)) {
        logoutSession();
      }
    }, 60_000);

    return () => {
      clearTimeout(timerId);
      clearInterval(intervalId);
    };
  }, [userToken, refreshProfile]);

  return (
    <UserContext.Provider value={{
      userToken,
      setUserToken,
      showLogin,
      setShowLogin,
      showMyBotSetup,
      setShowMyBotSetup,
      showCurrentlyRunning,
      setShowCurrentlyRunning,
      profile,
      setProfile,
      refreshProfile,
    }}>
      {children}
    </UserContext.Provider>
  );
};